import CustomDatePicker from "@Common/CustomDatePicker";
import { loaderState, PopupOverlayNewCalender } from "@store/index";
import dayjs, { Dayjs } from "dayjs";
import { useContext, useEffect, useMemo, useState, useCallback } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useRecoilState, useSetRecoilState } from "recoil";
import {
  PricingTabWrapper,
  ChannelsSection,
  ChannelsHeader,
  ChannelsHeaderText,
  ChannelsHeaderIcon,
  ChannelsList,
  ChannelItem,
  ChannelItemContent,
  ChannelIconContainer,
  ChannelName,
  ChannelValue,
  ChannelMoneyIcon,
  NoChannelsMessage,
} from "./styles";
import { IResponseGetListingManagement } from "src/services/calender/getListingManagement/types";
import { APIS_KEYS, useCalendar, usePostAllPlatformPrice } from "@services";
import { channelSettings, ChannelSetting } from "@services";
import Input from "@Common/input";
import { FORM_REGEX_VALIDATORS } from "@utils";
// import ChannelsListActive from "../channelsListActive"
import { ToastMessageContext } from "@context/toast-message-cotnext";
import GroupedPropertySelect, { CategoryGroup } from "../GroupedPropertySelect";
import WheelHouse from "./wheelHouse";
import { useIsFreemium } from "@hooks/useIsFreemium";
import { BsChevronUp } from "react-icons/bs";
import { Skeleton, Switch } from "@mui/material";

import MoneySvg from "@assets/svg/HSvg.svg?react";
import GatherInIcon from "@assets/svg/GatherInSoon.svg?react";
import AirbnbLogo from "@assets/svg/AirbnbLogo.svg?react";
import BookingLogo from "@assets/svg/BookingLogo.svg?react";
import Chevron_up from "@assets/svg/Chevron_up.svg?react";
import Chevron_down from "@assets/svg/Chevron_down.svg?react";
import { useQueryClient } from "@tanstack/react-query";
import { getChannelIcon, getChannelDisplayName } from "@utils";
import DaysSelector from "./DaysSelector";

interface IForm {
  date_from: dayjs.Dayjs | string;
  date_to: dayjs.Dayjs | string;
  propertiesId: string[];
  rate: string;
}
interface IProps {
  getListingManagement: IResponseGetListingManagement | undefined;
  categoryGroups: CategoryGroup[];
}
export default function PricingTab({ getListingManagement, categoryGroups }: IProps) {
  /* ------------------------------- Local State ------------------------------ */
  const [isPopupOverlayNewCalender, setPopupOverlayNewCalender] =
    useRecoilState(PopupOverlayNewCalender);
  const setLoaderState = useSetRecoilState(loaderState);
  const [isChannelsExpanded, setIsChannelsExpanded] = useState(true);
  // Freemium has no OTAs — the channel-commission section is hidden entirely.
  const isFreemium = useIsFreemium();
  /* ---------------------------------- Hooks --------------------------------- */
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const methods = useForm<IForm>({
    mode: "onChange",
  });
  const { setValue, watch, reset } = methods;
  const date_from_watch = watch("date_from");
  const date_to_watch = watch("date_to");
  const propertiesId_watch = watch("propertiesId");
  const rate_watch = watch("rate");
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useContext(ToastMessageContext);
  const [isChecked, setIsChecked] = useState(false);
  const [selectedDays, setSelectedDays] = useState<number[]>([]);

  /* ---------------------------------- API'S --------------------------------- */
  const { mutateAsync: mutateAsyncAllPlatformPrice } =
    usePostAllPlatformPrice();

  // Channel settings API for name-based lookup
  const { useGetChannelSettings, useUpdateWeekDaysPrice } = channelSettings();
  const { data: channelSettingsData, isLoading: isChannelSettingsLoading } =
    useGetChannelSettings();

  const { mutateAsync: mutateAsyncUpdateWeekDaysPrice } =
    useUpdateWeekDaysPrice();

  // Pure channel settings approach - display channels directly from settings API
  const displayChannels = useMemo(() => {
    if (!channelSettingsData?.result) return [];

    const basePrice = parseFloat(rate_watch) || 0;

    return channelSettingsData?.result?.map((channel: ChannelSetting) => {
      const normalizedName = channel?.name.toLowerCase().trim();
      const commission =
        channel.rates &&
        Array.isArray(channel.rates) &&
        channel.rates.length > 0
          ? parseFloat(channel.rates[0].value) || 0
          : 0;

      // Calculate price after adding commission - always show calculation even if basePrice is 0
      const priceAfterCommission =
        basePrice >= 0
          ? (basePrice + (basePrice * commission) / 100).toFixed(2)
          : "0.00";

      return {
        id: channel?.id,
        channelName: channel?.name,
        commission,
        displayName: getChannelDisplayName(channel?.name),
        channelKey: normalizedName,
        isActive: true, // Assuming all channels in settings are active
        priceAfterCommission,
      };
    });
  }, [channelSettingsData, rate_watch]);

  /* -------------------------------- Function -------------------------------- */
  const validMidDays: number[] = [0, 1, 2, 3]; // Sunday to Wednesday
  const validThuDays: number[] = [4]; // Thursday
  const validFriDays: number[] = [5]; // Friday
  const validSatDays: number[] = [6]; // Saturday

  function includesMidDays(start: Dayjs, end: Dayjs): "mid" | false {
    let currentDate: Dayjs = start;

    while (currentDate.isBefore(end) || currentDate.isSame(end)) {
      if (validMidDays.includes(currentDate.day())) return "mid";
      currentDate = currentDate.add(1, "day");
    }

    return false; // Return false if no valid day is found
  }
  function includesThuDays(start: Dayjs, end: Dayjs): "thu" | false {
    let currentDate: Dayjs = start;

    while (currentDate.isBefore(end) || currentDate.isSame(end)) {
      if (validThuDays.includes(currentDate.day())) return "thu";
      currentDate = currentDate.add(1, "day");
    }

    return false; // Return false if no valid day is found
  }
  function includesFriDays(start: Dayjs, end: Dayjs): "fri" | false {
    let currentDate: Dayjs = start;

    while (currentDate.isBefore(end) || currentDate.isSame(end)) {
      if (validFriDays.includes(currentDate.day())) return "fri";
      currentDate = currentDate.add(1, "day");
    }

    return false; // Return false if no valid day is found
  }

  function includesSatDays(start: Dayjs, end: Dayjs): "sat" | false {
    let currentDate: Dayjs = start;

    while (currentDate.isBefore(end) || currentDate.isSame(end)) {
      if (validSatDays.includes(currentDate.day())) return "sat";
      currentDate = currentDate.add(1, "day");
    }

    return false; // Return false if no valid day is found
  }

  const dataList = useMemo(() => {
    const data = getListingManagement?.result.map((item) => {
      return {
        value: item.propertyId.toString(),
        label: item.propertyName,
      };
    });

    return data ?? [];
  }, [getListingManagement]);

  const listOfChannels = useMemo(() => {
    const list = getListingManagement?.result?.filter((item) =>
      propertiesId_watch?.includes(item?.propertyId?.toString())
    );

    const channels = list?.flatMap((item) => item.connectedChannels);

    const listChannels = channels?.map((item) => {
      return {
        channelId: item.channelId,
        channelName: item.channelName,
        priceAfterCommission: item.commission,
      };
    });
    const uniqueChannels = listChannels?.filter(
      (channel, index, self) =>
        index === self.findIndex((c) => c.channelId === channel.channelId)
    );
    return uniqueChannels ?? [];
  }, [propertiesId_watch, getListingManagement]);

  const onSubmit = async (data: IForm) => {
    if (isChecked && selectedDays.length === 0) return;

    setLoaderState(true);
    const date_from = dayjs(data.date_from).format("YYYY-MM-DD");
    const date_to = dayjs(data.date_to).format("YYYY-MM-DD");
    const airbnb =
      listOfChannels.filter((item) => item.channelId === 1).length !== 0
        ? {
            rate: data.rate,
            date_from: date_from,
            date_to: date_to,
            min_stay: "0",
          }
        : null;
    const gathren =
      listOfChannels.filter((item) => item.channelId === 2).length !== 0
        ? {
            mid: includesMidDays(data.date_from as Dayjs, data.date_to as Dayjs)
              ? data.rate
              : "0",
            thu: includesThuDays(data.date_from as Dayjs, data.date_to as Dayjs)
              ? data.rate
              : "0",
            fri: includesFriDays(data.date_from as Dayjs, data.date_to as Dayjs)
              ? data.rate
              : "0",
            sat: includesSatDays(data.date_from as Dayjs, data.date_to as Dayjs)
              ? data.rate
              : isPopupOverlayNewCalender?.saturdayPrice ?? "0",
          }
        : null;
    const booking =
      listOfChannels.filter((item) => item.channelId === 3).length !== 0
        ? {
            date_from: date_from,
            date_to: date_to,
            rate: data.rate,
          }
        : null;
    const responseData = {
      propertiesId: data?.propertiesId,
      categoriesId: [],
      date_from: date_from,
      date_to: date_to,
      // Root-level rate so the price still reaches the backend for a freemium /
      // direct property (which has no connected channels, so airbnb/gathren/booking
      // are all null). For OTA properties the nested objects still take precedence.
      rate: data.rate,
      min_stay: "0",
      airbnb: airbnb,
      gathren: gathren,
      booking: booking,
    };

    if (isChecked && selectedDays.length > 0) {
      const weekDaysPriceData = {
        propertyIds: data?.propertiesId.map((id) => parseInt(id)),
        dateFrom: date_from,
        dateTo: date_to,
        weekDays: selectedDays,
        price: +data.rate,
      };

      try {
        const response = await mutateAsyncUpdateWeekDaysPrice(weekDaysPriceData);
        if (response?.status === 200) {
          reset();
          setPopupOverlayNewCalender(undefined);
          queryClient.invalidateQueries({
            queryKey: [APIS_KEYS.GETCALENDERDATA],
          });
          showSuccess(t("CALENDAR.PRICE_UPDATE_SUCCESS"));
        } else {
          const messages = response?.data?.errorMessages;
          if (messages?.length) {
            messages.forEach((item: any) =>
              showError(language === "ar" ? item.messageAr : item.messageEn)
            );
          }
        }
      } catch (error: any) {
        const messages =
          error?.response?.data?.errorMessages;
        if (messages?.length) {
          messages.forEach((item: any) =>
            showError(language === "ar" ? item.messageAr : item.messageEn)
          );
        }
      } finally {
        setLoaderState(false);
      }
      return;
    }

    try {
      const response = await mutateAsyncAllPlatformPrice({ ...responseData });
      if (response?.status === 200) {
        reset();
        setPopupOverlayNewCalender(undefined);
        queryClient.invalidateQueries({ queryKey: [APIS_KEYS.GETCALENDERDATA] });
        showSuccess(t("CALENDAR.PRICE_UPDATE_SUCCESS"));
      } else {
        const messages = response?.data?.errorMessages;
        if (messages?.length) {
          messages.forEach((item: any) =>
            showError(language === "ar" ? item.messageAr : item.messageEn)
          );
        }
      }
    } catch (error: any) {
      const messages = error?.response?.data?.errorMessages;
      if (messages?.length) {
        messages.forEach((item: any) =>
          showError(language === "ar" ? item.messageAr : item.messageEn)
        );
      }
    } finally {
      setLoaderState(false);
    }
  };

  /* ----------------------------- Action on Init ----------------------------- */
  useEffect(() => {
    if (isPopupOverlayNewCalender?.from && isPopupOverlayNewCalender?.to) {
      setValue("date_from", isPopupOverlayNewCalender?.from);
      setValue("date_to", isPopupOverlayNewCalender?.to.subtract(1, "day"));
    }
  }, [isPopupOverlayNewCalender]);

  useEffect(() => {
    if (!isPopupOverlayNewCalender?.propertyValue?.value) return
    const selId = isPopupOverlayNewCalender.propertyValue.value.toString()
    const group = categoryGroups.find((g) =>
      g.propertyIds.map(String).includes(selId)
    )
    setValue("propertiesId", group ? group.propertyIds.map(String) : [selId])
  }, [isPopupOverlayNewCalender, categoryGroups, setValue]);

  // Set initial rate value to ensure price after commission is always displayed
  useEffect(() => {
    if (
      isPopupOverlayNewCalender?.price &&
      isPopupOverlayNewCalender.price.length > 0
    ) {
      setValue("rate", isPopupOverlayNewCalender.price[0].toString());
    }
  }, [isPopupOverlayNewCalender, setValue]);

  return (
    <PricingTabWrapper language={language as "ar" | "en"}>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)} id="pricingForm">
          <div className="containerDate">
            <label>{t("COMMON.DATE")}</label>
            <div className="dateInputs">
              <CustomDatePicker
                disablePast
                name="date_from"
                label={t("GENERAL.FROM")}
                registerOptions={{
                  required: {
                    value: true,
                    message: t("COMMON.REQUIRED"),
                  },
                }}
              />
              <CustomDatePicker
                disablePast
                name="date_to"
                label={t("GENERAL.TO")}
                registerOptions={{
                  required: {
                    value: true,
                    message: t("COMMON.REQUIRED"),
                  },
                  min: {
                    value: date_from_watch as string,
                    message: t(
                      "COMMON.PLEAS_CHOOSE_A_CHECK_OUT_DATE_AFTER_YOUR_CHECK_IN_DATE"
                    ),
                  },
                }}
              />
            </div>

            <div className="numberOfNights">
              <p>{t("FINANCE.INCOME.NIGHTS")}</p>
              <span>
                {dayjs(date_to_watch).format("YYYY-MM-DD") !== "Invalid Date" &&
                dayjs(date_from_watch).format("YYYY-MM-DD") !== "Invalid Date"
                  ? Math.round(
                      dayjs(date_to_watch)?.diff(date_from_watch, "d", true)
                    ) +
                      1 <
                    1
                    ? "-"
                    : Math.round(
                        dayjs(date_to_watch)?.diff(date_from_watch, "d", true)
                      ) + 1
                  : 0}
              </span>
            </div>
          </div>

          <div className="byProperty">
            <GroupedPropertySelect
              name="propertiesId"
              label={t("COMMON.PROPERTY")}
              categoryGroups={categoryGroups}
              allProperties={dataList ?? []}
              registerOptions={{
                required: {
                  value: true,
                  message: t("COMMON.REQUIRED"),
                },
              }}
            />
          </div>
          {!isPopupOverlayNewCalender?.isWheelhouseAutomationEnable && (
            <>
              <Input
                suffixLabel={t("COMMON.RS")}
                name="rate"
                label={t("COMMON.PRICING")}
                placeholder={
                  isPopupOverlayNewCalender?.price &&
                  isPopupOverlayNewCalender?.price.length > 1
                    ? `${isPopupOverlayNewCalender?.price[0]} - ${isPopupOverlayNewCalender?.price[isPopupOverlayNewCalender?.price.length - 1]}`
                    : isPopupOverlayNewCalender?.price &&
                        isPopupOverlayNewCalender?.price.length === 1
                      ? `${isPopupOverlayNewCalender?.price[0]} `
                      : ""
                }
                registerOptions={{
                  required: {
                    value: true,
                    message: t("COMMON.REQUIRED"),
                  },
                  min: {
                    value: 38,
                    message: t("ERRORS.MINIMUM_VALUE", { number: 38 }),
                  },
                  max: {
                    value: 50000,
                    message: t("ERRORS.MINIMUM_VALUE", { number: 50000 }),
                  },
                  pattern: {
                    value: FORM_REGEX_VALIDATORS.numbersOnly,
                    message: t("COMMON.THE_FIELD_ACCEPTS_NUMBERS_ONLY"),
                  },
                }}
              />
              {/* MS-519: static notice — fees + VAT are added on top for the guest. */}
              <p className="text-xs text-gray-500 mt-2 mb-3 leading-5">
                {t("COMMON.FEES_VAT_NOTICE")}
              </p>
              <div className="flex justify-between items-center mb-4">
                <div>
                  <p className="text-indigo-700 text-base">
                    {t("FINANCE.WEEKDAY_PRICE_CUSTOMIZATION")}
                  </p>
                  <p className="text-gra font-normal">
                    {t("FINANCE.WEEKDAY_PRICE_CUSTOMIZATION_DESCRIPTION")}
                  </p>
                </div>
                <div>
                  <Switch
                    checked={isChecked}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setIsChecked(e.target.checked)
                    }
                  />
                </div>
              </div>
              {isChecked && (
                <div className="mb-4">
                  <DaysSelector
                    selectedDays={selectedDays}
                    setSelectedDays={(days) => setSelectedDays(days)}
                  />
                  {selectedDays.length === 0 && (
                    <p className="text-red-500">
                      {t("FINANCE.SELECT_AT_LEAST_ONE_DAY")}
                    </p>
                  )}
                </div>
              )}
            </>
          )}

          {/* <ChannelsListActive /> */}
        </form>
      </FormProvider>
      {isPopupOverlayNewCalender?.isWheelhouseActive &&
        !isPopupOverlayNewCalender?.isWheelhouseAutomationEnable &&
        isPopupOverlayNewCalender?.wheelhouseRecommendationPrice && (
          <WheelHouse
            date_from={date_from_watch ?? ""}
            date_to={date_to_watch ?? ""}
            propertyIds={propertiesId_watch ?? []}
            priceWheelHouse={
              isPopupOverlayNewCalender?.wheelhouseRecommendationPrice &&
              isPopupOverlayNewCalender?.wheelhouseRecommendationPrice.length >
                1
                ? `${isPopupOverlayNewCalender?.wheelhouseRecommendationPrice[0]} - ${isPopupOverlayNewCalender?.wheelhouseRecommendationPrice[isPopupOverlayNewCalender?.wheelhouseRecommendationPrice.length - 1]}`
                : isPopupOverlayNewCalender?.wheelhouseRecommendationPrice &&
                    isPopupOverlayNewCalender?.wheelhouseRecommendationPrice
                      .length === 1
                  ? `${isPopupOverlayNewCalender?.wheelhouseRecommendationPrice[0]} `
                  : ""
            }
          />
        )}
      {/* Channel commission — OTA-only, hidden for Freemium */}
      {!isFreemium && (
      <ChannelsSection>
        <ChannelsHeader
          onClick={() => setIsChannelsExpanded(!isChannelsExpanded)}
        >
          <ChannelsHeaderText>
            {t("COMMON.CHANNEL_COMMISSION")}
          </ChannelsHeaderText>
        </ChannelsHeader>

        <ChannelsList>
          {isChannelSettingsLoading ? (
            Array.from({ length: 3 }).map((_, index) => (
              <ChannelItem key={`skeleton-${index}`}>
                <Skeleton
                  style={{ marginInline: "12px" }}
                  variant="circular"
                  width={24}
                  height={24}
                />
                <Skeleton variant="text" width={"25%"} height={15} />
              </ChannelItem>
            ))
          ) : displayChannels?.length ? (
            displayChannels.map((item) => (
              <ChannelItem key={item.id}>
                <ChannelItemContent>
                  <ChannelIconContainer>
                    {getChannelIcon(item.channelKey || item.channelName)}
                  </ChannelIconContainer>
                  <ChannelName>
                    {item.displayName
                      ? `${item.displayName} - (${item.commission}%)`
                      : item.channelName}
                  </ChannelName>

                  <ChannelValue>
                    {item.priceAfterCommission}
                    <ChannelMoneyIcon>
                      <MoneySvg />
                    </ChannelMoneyIcon>
                  </ChannelValue>
                </ChannelItemContent>
              </ChannelItem>
            ))
          ) : (
            // Show message when no channels
            <NoChannelsMessage>
              {t("COMMON.NO_CHANNELS_AVAILABLE")}
            </NoChannelsMessage>
          )}
        </ChannelsList>
      </ChannelsSection>
      )}
    </PricingTabWrapper>
  );
}
